import { useEffect, useRef } from 'react';

const STICKY_SIZE_DESKTOP = 320;
const STICKY_SIZE_MOBILE = 240;

interface UseStickyVideoPlayerProps {
	/** The player host element that scrolls with the page and gets observed against the viewport. */
	container?: HTMLElement;
	/** Whether sticky behaviour is allowed for this player. */
	enabled: boolean;
	/** Only go sticky while the video is actually playing. */
	isPlaying: boolean;
	/** Skip while the player is in fullscreen — a sticky corner box is irrelevant there. */
	isFullScreen: boolean;
	/** Current sticky state from context — used to detect the sticky→inline transition. */
	isSticky: boolean;
	/** GoBrain player widget ID, used to restore playback position after layout normalises. */
	currentPlayerId: string;
	/** Current playback position — tracked so we can re-seek if GoBrain resets it on restore. */
	currentPosition: number | string;
	/** Live streams don't need position restore. */
	isMediaLive: boolean;
	/** Stick/unstick the mini-player. */
	setIsSticky: (_value: boolean) => void;
	/** Called just before the player goes sticky — use it to close sidebars / clean up overlays. */
	onBeforeSticky?: () => void;
	/** Loaded media list — used to read the current media's video dimensions for the mini-player aspect ratio. */
	mediasData?: any[];
	/** ID of the currently active media. */
	currentMediaId?: string;
	/** Raw value of additionalSettings.stickyVideoPlayerSize. Omit to use the platform default. */
	stickyVideoPlayerSize?: number;
	/** True when the player is running on a mobile device — used to pick the correct default size. */
	isUsedOnMobile: boolean;
}

/**
 * Sticky video player: when enabled and the video is playing, scrolling the player out of the
 * viewport docks it as a fixed corner mini-player, and scrolling it back into view restores it
 * inline (YouTube-style). Unlike native PiP this needs no user gesture, so it fires reliably
 * on every scroll and works in every browser.
 *
 * The hook manages the full sticky lifecycle:
 *  - IntersectionObserver to detect when the player leaves/enters the viewport
 *  - Host height freeze (before fixed positioning) so the page layout doesn't collapse
 *  - Host height clear + GoBrain position guard on restore
 *  - --sticky-width/height CSS variables on the container, derived from the media's actual dimensions
 */
export const useStickyVideoPlayer = ({
	container,
	enabled,
	isPlaying,
	isFullScreen,
	isSticky,
	currentPlayerId,
	currentPosition,
	isMediaLive,
	setIsSticky,
	onBeforeSticky,
	mediasData,
	currentMediaId,
	stickyVideoPlayerSize,
	isUsedOnMobile,
}: UseStickyVideoPlayerProps) => {
	const stickyPlayerSize = stickyVideoPlayerSize ?? (isUsedOnMobile ? STICKY_SIZE_MOBILE : STICKY_SIZE_DESKTOP);
	// Latest prop values, read inside the observer callback (created once per container).
	const enabledRef = useRef(enabled);
	const isPlayingRef = useRef(isPlaying);
	const isFullScreenRef = useRef(isFullScreen);
	const setIsStickyRef = useRef(setIsSticky);
	const onBeforeStickyRef = useRef(onBeforeSticky);
	const mediasDataRef = useRef(mediasData);
	const currentMediaIdRef = useRef(currentMediaId);
	const stickyPlayerSizeRef = useRef(stickyPlayerSize);


	// True while WE docked the player automatically — so we only auto-restore what we stickied.
	const autoStickyRef = useRef(false);
	// Set when the user dismisses the mini-player while still scrolled away; prevents re-docking
	// until the player scrolls back into view.
	const suppressUntilInViewRef = useRef(false);

	// Track the last non-zero playback position so we can re-seek if GoBrain resets to 0 on restore.
	const currentPositionRef = useRef<number>(0);
	const lastGoodPositionRef = useRef<number>(0);

	useEffect(() => {
		enabledRef.current = enabled;
		isPlayingRef.current = isPlaying;
		isFullScreenRef.current = isFullScreen;
		setIsStickyRef.current = setIsSticky;
		onBeforeStickyRef.current = onBeforeSticky;
		mediasDataRef.current = mediasData;
		currentMediaIdRef.current = currentMediaId;
		stickyPlayerSizeRef.current = stickyPlayerSize;
	}, [enabled, isPlaying, isFullScreen, setIsSticky, onBeforeSticky, mediasData, currentMediaId, stickyVideoPlayerSize, isUsedOnMobile]);

	// Keep position refs in sync every render tick.
	useEffect(() => {
		const pos = typeof currentPosition === 'number' ? currentPosition : 0;
		currentPositionRef.current = pos;
		if (pos > 0) {
			lastGoodPositionRef.current = pos;
		}
	}, [currentPosition]);

	// IntersectionObserver: go sticky when player leaves viewport, restore when it re-enters.
	useEffect(() => {
		if (!container || typeof IntersectionObserver === 'undefined') {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (!entry) {
					return;
				}

				if (!entry.isIntersecting) {
					// Player scrolled out of the viewport → dock as a sticky mini-player.
					if (
						enabledRef.current &&
						isPlayingRef.current &&
						!isFullScreenRef.current &&
						!suppressUntilInViewRef.current &&
						!autoStickyRef.current
					) {
						autoStickyRef.current = true;
						onBeforeStickyRef.current?.();
						// Compute mini-player dimensions from the configured size and the media's
						// actual aspect ratio. For landscape: size = width. For portrait: size = height.
						const media = mediasDataRef.current?.find(
							({ id, originalId }: any) =>
								id === currentMediaIdRef.current || originalId === currentMediaIdRef.current
						);
						const size = stickyPlayerSizeRef.current;
						let w: number;
						let h: number;
						if (media?.videoWidth && media?.videoHeight) {
							const ratio = media.videoWidth / media.videoHeight;
							if (ratio >= 1) {
								w = size;
								h = Math.round(size / ratio);
							} else {
								h = size;
								w = Math.round(size * ratio);
							}
						} else {
							// Fallback: assume 16:9 landscape.
							w = size;
							h = Math.round(size * 9 / 16);
						}
						container.style.setProperty('--sticky-width', `${w}px`);
						container.style.setProperty('--sticky-height', `${h}px`);
						// Freeze the host height NOW, while the player is still in normal flow, so
						// the surrounding page keeps its space once .qplayer-app becomes fixed.
						container.style.height = `${container.offsetHeight}px`;
						setIsStickyRef.current(true);
					}
				} else {
					// Player scrolled back into view → restore inline.
					suppressUntilInViewRef.current = false;
					if (autoStickyRef.current) {
						autoStickyRef.current = false;
						container.style.removeProperty('--sticky-width');
						container.style.removeProperty('--sticky-height');
						setIsStickyRef.current(false);
					}
				}
			},
			// threshold 0 → toggles exactly when the host fully enters/leaves the viewport.
			{ threshold: 0 }
		);

		observer.observe(container);

		return () => {
			observer.disconnect();
			container.style.height = '';
			container.style.removeProperty('--sticky-width');
			container.style.removeProperty('--sticky-height');
		};
	}, [container]);

	// Set by dismiss() so the restore guard skips re-play when the user explicitly closed
	// the mini-player (vs auto-restoring on scroll-back, where we do want to resume).
	const userDismissedRef = useRef(false);

	// Restore effect: fires on the sticky→inline transition.
	// Clears the frozen host height and guards against GoBrain resetting position to 0.
	const prevStickyRef = useRef(false);
	useEffect(() => {
		if (prevStickyRef.current && !isSticky) {
			if (container) {
				container.style.height = '';
				container.style.removeProperty('--sticky-width');
				container.style.removeProperty('--sticky-height');
			}
			const targetPos = lastGoodPositionRef.current;
			if (!isMediaLive && targetPos > 1) {
				const shouldResume = isPlaying === true && !userDismissedRef.current;
				const widget = (window as any).GoBrain?.widgets?.(currentPlayerId);
				// GoBrain may reset to 0 a moment after the layout normalises; poll briefly and
				// re-seek once as soon as a drop is detected (clears within ~1s).
				let attempts = 0;
				const guard = setInterval(() => {
					attempts += 1;
					if (currentPositionRef.current < targetPos - 2) {
						widget?.position?.(targetPos);
						shouldResume && widget?.play?.();
						clearInterval(guard);
					} else if (attempts >= 10) {
						clearInterval(guard);
					}
				}, 100);
			}
			userDismissedRef.current = false;
		}
		prevStickyRef.current = isSticky;
	}, [isSticky, container, currentPlayerId, isMediaLive, isPlaying]);

	// Dismiss: pause the player, unstick, and don't re-stick until the player scrolls back into view.
	const dismissStickyPlayer = () => {
		autoStickyRef.current = false;
		suppressUntilInViewRef.current = true;
		userDismissedRef.current = true;
		if (container) {
			container.style.removeProperty('--sticky-width');
			container.style.removeProperty('--sticky-height');
		}
		(window as any).GoBrain?.widgets?.(currentPlayerId)?.pause?.();
		setIsStickyRef.current(false);
	};

	return { dismissStickyPlayer };
};
